/**
 * Global Assistant Context
 *
 * Provides a unified assistant interface accessible from anywhere in the application.
 * Manages panel state, chat history, model selection, and page context registration.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  streamChat,
  UnifiedContext,
  ContextType,
  EditorContext,
  PlaygroundContext,
  ExecutionContext,
  EvalContext,
  SchemaContext,
  TrajectoryContext,
  EditorAction,
  clearSessionCache,
} from '../services/globalAssistantApi';
import {
  listConversations as apiListConversations,
  getConversation as apiGetConversation,
  linkTrajectory as apiLinkTrajectory,
  Conversation,
  decodeContent,
} from '../services/conversationApi';
import { useAuth } from './AuthContext';

// =============================================================================
// Types
// =============================================================================

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCall[];
  isStreaming?: boolean;
}

export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
  result?: Record<string, unknown>;
  status: 'pending' | 'running' | 'complete' | 'error';
}

export interface PageContext {
  contextType: ContextType;
  editor?: EditorContext;
  playground?: PlaygroundContext;
  execution?: ExecutionContext;
  evals?: EvalContext;
  schemas?: SchemaContext;
  trajectories?: TrajectoryContext;
}

export interface ModelInfo {
  id: string;
  name: string;
  default?: boolean;
}

// Editor action handler type
export type EditorActionHandler = (action: EditorAction) => void;

interface GlobalAssistantContextValue {
  // Panel state
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  panelWidth: number;
  setPanelWidth: (width: number) => void;

  // Page context
  pageContext: PageContext;
  registerPageContext: (context: Partial<PageContext>) => void;
  clearPageContext: () => void;

  // Chat state
  messages: Message[];
  isLoading: boolean;
  error: string | null;

  // Chat actions
  sendMessage: (content: string, images?: File[]) => Promise<void>;
  clearMessages: () => void;
  stopGeneration: () => void;

  // Model selection
  model: string | null;
  setModel: (model: string) => void;
  availableModels: ModelInfo[];
  modelsLoading: boolean;

  // API key status
  hasAnyKeys: boolean | null;  // null = loading, false = no keys, true = has keys
  configuredProviders: string[];
  refreshAvailableModels: () => Promise<void>;

  // Editor action callback (for pages that support editing)
  registerEditorActionHandler: (handler: EditorActionHandler | null) => void;

  // Conversation management
  currentConversationId: string | null;
  conversations: Conversation[];
  conversationsLoading: boolean;
  startNewConversation: () => void;
  loadConversation: (id: string) => Promise<void>;
  loadConversationList: () => Promise<void>;
  linkCurrentTrajectory: (trajectoryId: string) => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const GlobalAssistantContext = createContext<GlobalAssistantContextValue | null>(null);

// =============================================================================
// Constants
// =============================================================================

// No hardcoded fallback models - let API determine available models based on keys
const STORAGE_KEY = 'global_assistant_state';
const PANEL_WIDTH_KEY = 'global_assistant_panel_width';
const DEFAULT_PANEL_WIDTH = 380;
const MAX_STORED_MESSAGES = 50;

interface StoredState {
  messages: Message[];
  model: string | null;
}

function loadState(): StoredState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Keep stored model - API will validate if it's still available
      return {
        messages: (parsed.messages || []).filter((m: Message) => !m.isStreaming),
        model: parsed.model || null,
      };
    }
  } catch (e) {
    console.warn('Failed to load global assistant state:', e);
  }
  return { messages: [], model: null };
}

function saveState(state: StoredState): void {
  try {
    const toSave = {
      messages: state.messages.filter((m) => !m.isStreaming).slice(-MAX_STORED_MESSAGES),
      model: state.model,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn('Failed to save global assistant state:', e);
  }
}

// =============================================================================
// Provider
// =============================================================================

interface GlobalAssistantProviderProps {
  children: ReactNode;
}

export function GlobalAssistantProvider({ children }: GlobalAssistantProviderProps) {
  // Load initial state from localStorage
  const initialState = loadState();

  // Get auth state to ensure we wait for auth before fetching models
  const { isAuthenticated, activeTeam, isLoading: authLoading } = useAuth();

  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [panelWidth, setPanelWidthState] = useState(() => {
    try {
      const stored = localStorage.getItem(PANEL_WIDTH_KEY);
      return stored ? parseInt(stored, 10) : DEFAULT_PANEL_WIDTH;
    } catch {
      return DEFAULT_PANEL_WIDTH;
    }
  });

  // Save panel width to localStorage
  const setPanelWidth = useCallback((width: number) => {
    setPanelWidthState(width);
    try {
      localStorage.setItem(PANEL_WIDTH_KEY, width.toString());
    } catch (e) {
      console.warn('Failed to save panel width:', e);
    }
  }, []);

  // Page context
  const [pageContext, setPageContext] = useState<PageContext>({
    contextType: 'general',
  });

  // Chat state
  const [messages, setMessages] = useState<Message[]>(initialState.messages);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Model selection - start with empty until API returns available models
  const [model, setModel] = useState<string | null>(initialState.model);
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [hasAnyKeys, setHasAnyKeys] = useState<boolean | null>(null);
  const [configuredProviders, setConfiguredProviders] = useState<string[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  // Conversation management
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // Abort controller for cancelling requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Editor action handler (registered by pages that support editing)
  const editorActionHandlerRef = useRef<EditorActionHandler | null>(null);

  const registerEditorActionHandler = useCallback((handler: EditorActionHandler | null) => {
    editorActionHandlerRef.current = handler;
  }, []);

  // Fetch available models based on configured API keys
  const refreshAvailableModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const { getSessionToken } = await import('../services/globalAssistantApi');
      const token = await getSessionToken();
      if (!token) {
        // No session token - can't determine available models
        setHasAnyKeys(null);
        setAvailableModels([]);
        setModelsLoading(false);
        return;
      }

      const response = await fetch('/api/assistant/global/models/available', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setHasAnyKeys(data.has_any_keys);
        setConfiguredProviders(data.configured_providers || []);

        const models = (data.models || []).map((m: any) => ({
          id: m.id,
          name: m.name,
          default: m.id === data.default,
        }));
        setAvailableModels(models);

        // Set model: use current if available, otherwise use API default
        if (models.length > 0) {
          const currentModelAvailable = model && models.some((m: ModelInfo) => m.id === model);
          if (!currentModelAvailable && data.default) {
            setModel(data.default);
          } else if (!currentModelAvailable && !data.default) {
            // No default from API, use first available
            setModel(models[0].id);
          }
        } else {
          // No models available
          setModel(null);
        }
      } else {
        // API error - no models available
        setHasAnyKeys(false);
        setAvailableModels([]);
        setModel(null);
      }
    } catch (err) {
      console.warn('Failed to fetch available models:', err);
      setHasAnyKeys(null);
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, [model]);

  // Fetch available models when auth is ready and team changes
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Clear session cache when team changes to force fresh token with correct team_id
    clearSessionCache();

    // Only fetch if authenticated with a team
    if (isAuthenticated && activeTeam) {
      refreshAvailableModels();
    } else if (!isAuthenticated) {
      // Not authenticated - clear models
      setAvailableModels([]);
      setHasAnyKeys(null);
      setModelsLoading(false);
    }
  }, [authLoading, isAuthenticated, activeTeam?.id]); // Re-run when auth state or team changes

  // Also refresh when panel opens (to get latest keys)
  useEffect(() => {
    if (isOpen) {
      refreshAvailableModels();
    }
  }, [isOpen]);

  // Save state to localStorage when messages or model change
  useEffect(() => {
    const hasStreaming = messages.some((m) => m.isStreaming);
    if (!hasStreaming) {
      saveState({ messages, model });
    }
  }, [messages, model]);

  // Panel controls
  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen((prev) => !prev), []);

  // Page context registration
  // When a new contextType is set, clear all other page-specific contexts
  const registerPageContext = useCallback((context: Partial<PageContext>) => {
    setPageContext((prev) => {
      // If contextType is changing, start fresh with only the new context
      if (context.contextType && context.contextType !== prev.contextType) {
        return {
          contextType: context.contextType,
          editor: context.editor,
          playground: context.playground,
          evals: context.evals,
          schemas: context.schemas,
          trajectories: context.trajectories,
        };
      }
      // Otherwise merge with previous context
      return {
        ...prev,
        ...context,
      };
    });
  }, []);

  const clearPageContext = useCallback(() => {
    setPageContext({ contextType: 'general' });
  }, []);

  // Convert file to base64
  const fileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Send message
  const sendMessage = useCallback(
    async (content: string, images?: File[]) => {
      if (!content.trim() && (!images || images.length === 0)) return;
      if (!model) {
        setError('No model selected. Please configure an API key in Settings.');
        return;
      }

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: content.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      // Create placeholder assistant message
      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        toolCalls: [],
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Create abort controller
      abortControllerRef.current = new AbortController();

      let accumulatedContent = '';
      let currentToolCalls: ToolCall[] = [];

      try {
        // Convert images to base64 if any
        const imageData: { data: string; media_type: string }[] = [];
        if (images && images.length > 0) {
          for (const file of images) {
            const base64 = await fileToBase64(file);
            imageData.push({
              data: base64,
              media_type: file.type,
            });
          }
        }

        // Build context for API
        const context: UnifiedContext = {
          context_type: pageContext.contextType,
          editor: pageContext.editor,
          playground: pageContext.playground,
          evals: pageContext.evals,
          schemas: pageContext.schemas,
          trajectories: pageContext.trajectories,
        };

        await streamChat(
          {
            messages: [...messages, userMessage].map((m) => ({
              role: m.role,
              content: m.content,
            })),
            context,
            model,
            images: imageData.length > 0 ? imageData : undefined,
            conversation_id: currentConversationId || undefined,
            persist: true,
          },
          {
            onConversation: (conversationId) => {
              setCurrentConversationId(conversationId);
            },
            onText: (text) => {
              accumulatedContent += text;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, content: accumulatedContent }
                    : m
                )
              );
            },
            onToolStart: (tool, input) => {
              const newToolCall: ToolCall = {
                id: `tool-${Date.now()}-${tool}`,
                name: tool,
                input,
                status: 'running',
              };
              currentToolCalls = [...currentToolCalls, newToolCall];
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, toolCalls: currentToolCalls }
                    : m
                )
              );
            },
            onToolResult: (tool, result) => {
              currentToolCalls = currentToolCalls.map((tc) =>
                tc.name === tool && tc.status === 'running'
                  ? { ...tc, result, status: 'complete' }
                  : tc
              );
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, toolCalls: currentToolCalls }
                    : m
                )
              );
            },
            onEditorAction: (action) => {
              // Call the registered editor action handler if one exists
              console.log('[GlobalAssistant] Editor action received:', action);
              if (editorActionHandlerRef.current) {
                console.log('[GlobalAssistant] Calling registered handler');
                editorActionHandlerRef.current(action);
              } else {
                console.warn('[GlobalAssistant] No editor action handler registered');
              }
            },
            onError: (message) => {
              setError(message);
            },
            onDone: () => {
              // Mark streaming as complete
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId ? { ...m, isStreaming: false } : m
                )
              );
            },
          },
          abortControllerRef.current.signal
        );
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? { ...m, isStreaming: false, content: m.content + ' [Stopped]' }
                : m
            )
          );
        } else {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setMessages((prev) => prev.filter((m) => m.id !== assistantMessageId));
        }
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [messages, model, pageContext, currentConversationId]
  );

  // Clear messages
  const clearMessages = useCallback(() => {
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Stop generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Conversation management functions
  const loadConversationList = useCallback(async () => {
    if (!isAuthenticated) return;
    setConversationsLoading(true);
    try {
      const response = await apiListConversations({
        status: 'active',
        pinned_first: true,
        limit: 50,
      });
      setConversations(response.conversations);
    } catch (err) {
      console.warn('Failed to load conversations:', err);
    } finally {
      setConversationsLoading(false);
    }
  }, [isAuthenticated]);

  const loadConversation = useCallback(async (id: string) => {
    try {
      const detail = await apiGetConversation(id);
      setCurrentConversationId(id);
      // Convert server messages to local Message format
      const loadedMessages: Message[] = detail.messages.map((m, index) => ({
        id: `loaded-${m.id}`,
        role: m.role,
        content: decodeContent(m.content),
        toolCalls: m.tool_calls?.map((tc, i) => ({
          id: `tool-${index}-${i}`,
          name: tc.name,
          input: tc.input,
          result: tc.result,
          status: 'complete' as const,
        })),
      }));
      setMessages(loadedMessages);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError('Failed to load conversation');
    }
  }, []);

  const startNewConversation = useCallback(() => {
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const linkCurrentTrajectory = useCallback(async (trajectoryId: string) => {
    if (!currentConversationId) {
      console.warn('No current conversation to link trajectory to');
      return;
    }
    try {
      await apiLinkTrajectory(currentConversationId, {
        trajectory_id: trajectoryId,
        link_type: 'reference',
      });
    } catch (err) {
      console.error('Failed to link trajectory:', err);
    }
  }, [currentConversationId]);

  // Load conversation list when authenticated and panel opens
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadConversationList();
    }
  }, [isOpen, isAuthenticated, loadConversationList]);

  const value: GlobalAssistantContextValue = {
    isOpen,
    openPanel,
    closePanel,
    togglePanel,
    panelWidth,
    setPanelWidth,
    pageContext,
    registerPageContext,
    clearPageContext,
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    stopGeneration,
    model,
    setModel,
    availableModels,
    modelsLoading,
    hasAnyKeys,
    configuredProviders,
    refreshAvailableModels,
    registerEditorActionHandler,
    currentConversationId,
    conversations,
    conversationsLoading,
    startNewConversation,
    loadConversation,
    loadConversationList,
    linkCurrentTrajectory,
  };

  return (
    <GlobalAssistantContext.Provider value={value}>
      {children}
    </GlobalAssistantContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useGlobalAssistant(): GlobalAssistantContextValue {
  const context = useContext(GlobalAssistantContext);
  if (!context) {
    throw new Error('useGlobalAssistant must be used within a GlobalAssistantProvider');
  }
  return context;
}

// =============================================================================
// Route Listener - Resets context on navigation
// =============================================================================

/**
 * Component that listens for route changes and resets the page context.
 * Must be placed inside the Router but can be anywhere in the component tree.
 * Pages that want specific context can register it via useEffect after this resets.
 */
export function GlobalAssistantRouteListener() {
  const location = useLocation();
  const { clearPageContext } = useGlobalAssistant();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    // Only reset context when the path actually changes
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;
      // Reset to general context - pages will register their specific context via useEffect
      clearPageContext();
    }
  }, [location.pathname, clearPageContext]);

  return null;
}
